import { useAppSelector } from "@app/hooks";
import { LRadio } from "@components";
import { LSelect, SelectOption } from "@components/LSelect";
import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import { useModal } from "@services/ModalContext";
import { ExistingUser, InviteUser, InviteUserArray, RoleId } from "@store/generalTypes";
import { useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string } from "yup";
import styles from "./AssignStaffUserModal.module.css";

enum Access {
    all = "Full location access",
    oneClinician = "Assigned to overseeing clinician"
}

interface SelectClinician {
    access: string;
    clinician: string;
}

const clinicianSelect = object({
    access: string(),
    clinician: string().when("access", (val) => {
        if (val === Access.oneClinician) {
            return string().required(DefaultErrorMessages.required);
        }
        return string();
    })
});

export const AssignClinicianStaffBody = ({
    staff,
    locationName,
    clinicians,
    locationId,
    setClinicianStaffUsersToAdd,
    goBack,
    toggleAssignToClinicianModal,
    batchAssignLocationUser
}: AssignClinicianStaffBodyProps): JSX.Element => {
    const validateMethods = useForm<SelectClinician>({
        resolver: yupResolver(clinicianSelect)
    });

    const [radioSelected, setRadioSelected] = useState<"all" | "one">("all");

    const { closeModal } = useModal();

    const organization = useAppSelector((x) => x.organization);

    const formSubmitHandler: SubmitHandler<SelectClinician> = async (data: SelectClinician) => {
        if (data.access === Access.all) {
            await batchAssignLocationUser(
                organization.id!,
                locationId,
                staff!.users,
                RoleId.ClinicalStaff,
                clinicians[0].id
            );
        } else {
            await batchAssignLocationUser(
                organization.id!,
                locationId,
                staff!.users,
                RoleId.ClinicalStaff,
                data.clinician
            );
        }

        closeModal();
    };

    return (
        <>
            {staff?.users.map((x, idx) => (
                <div key={`assignee-${idx}-${x.email}`} className={styles.selectedUser}>
                    {`${x.firstName} ${x.lastName} <${x.email}>`}
                    <button
                        className="has-background-primary-blue delete"
                        onClick={() => {
                            const deletedClinicians = staff.users.filter((cs) => x.email !== cs.email);
                            setClinicianStaffUsersToAdd({ users: deletedClinicians });
                            if (!deletedClinicians.length) {
                                toggleAssignToClinicianModal();
                                goBack();
                            }
                        }}
                    ></button>
                </div>
            ))}
            <div className="divider" />
            <p className="has-text-ldark-gray has-text-weight-bold">Please configure this user’s access</p>
            <div className="mt-4  is-flex is-flex-direction-column is-justify-content-center mw-90">
                <FormProvider {...validateMethods}>
                    <form id="clinicalStaff" onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                        <div>
                            <LRadio
                                id="all"
                                inputName="access"
                                value={Access.all}
                                classes="has-text-weight-bold has-text-ldark-gray"
                                checked={radioSelected === "all"}
                                onChecked={() => {
                                    setRadioSelected("all");
                                }}
                            />
                            <div className="pl-1 ml-5">
                                <p>
                                    Access to all identifiable patient data and results{" "}
                                    <span className="has-text-weight-bold has-text-ldark-gray">
                                        at this location ({locationName})
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="my-4">
                            <LRadio
                                id="one"
                                inputName="access"
                                value={Access.oneClinician}
                                classes="has-text-weight-bold has-text-ldark-gray "
                                checked={radioSelected === "one"}
                                onChecked={() => {
                                    setRadioSelected("one");
                                }}
                            />
                            <div className="pl-1 ml-5">
                                <p>
                                    Access to identifiable patient data and results restricted to what{" "}
                                    <span className="has-text-weight-bold has-text-ldark-gray">
                                        the overseeing clinician has access to
                                    </span>
                                </p>
                            </div>
                        </div>
                        {radioSelected === "one" && (
                            <div className="my-4">
                                <LSelect
                                    options={clinicians.map((x) => {
                                        return {
                                            name: `${x.firstName} ${x.lastName} <${x.email}>`,
                                            value: x.id
                                        } as SelectOption;
                                    })}
                                    label={"Clinician"}
                                    inputName="clinician"
                                    defaultOption={{ name: "Select clinician", value: "" } as SelectOption}
                                    isFullWidth
                                />
                            </div>
                        )}
                    </form>
                </FormProvider>
            </div>
        </>
    );
};

interface AssignClinicianStaffBodyProps {
    staff?: InviteUserArray;
    locationName: string;
    setClinicianStaffUsersToAdd: (users: InviteUserArray) => void;
    toggleAssignToClinicianModal: () => void;
    goBack: () => void;
    clinicians: ExistingUser[];
    locationId: number;
    batchAssignLocationUser: (
        organizationId: number,
        locationId: number,
        users: InviteUser[],
        roleId: RoleId,
        clinicianId?: string | undefined
    ) => Promise<void>;
}
